import { useTranslation } from 'react-i18next';

import useIsTruncated from 'hooks/useIsTruncated';
import { cn } from 'support/helpers/cn/cn';
import { PartnerDTO, PartnerV2 } from 'support/types';
import { WithRef } from 'support/types/withRef';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';

import { TradePartnerNameAndLogoClamping } from './types';

type PartnerWithLogo = Pick<PartnerV2, 'name' | 'logo_url'>;

type TradePartnerNameAndLogoProps = {
  partner: PartnerWithLogo | PartnerDTO | null | undefined;
  clamping?: TradePartnerNameAndLogoClamping;
  size?: 'base' | 'large' | 'small';
  logoConfiguration?: {
    className?: string;
  };
  textConfiguration?: {
    className?: string;
  };
};
export const TradePartnerNameAndLogo = ({
  partner,
  clamping = TradePartnerNameAndLogoClamping.VERTICAL,
  logoConfiguration,
  textConfiguration,
  size,
  ref,
}: WithRef<TradePartnerNameAndLogoProps, HTMLDivElement>) => {
  const { t } = useTranslation();
  const imageUrl = partner ? getPartnerLogoUrl(partner) : null;
  const [truncatedRef, valueIsTruncated] = useIsTruncated();
  return (
    <div className={cn('flex items-center', size === 'small' ? 'gap-2' : 'gap-4')} ref={ref}>
      <PartnerLogo
        imageUrl={imageUrl}
        size={size}
        className={logoConfiguration?.className}
        knownPartner={Boolean(partner)}
      />
      <div className="flex items-center">
        <Tooltip enabled={valueIsTruncated}>
          <TooltipTrigger>
            <span
              ref={truncatedRef}
              className={cn(
                'overflow-hidden',
                size === 'small' ? 'text-xs' : 'text-sm',
                textConfiguration?.className ? textConfiguration.className : 'font-medium text-gray-700',
                {
                  'line-clamp-2 max-h-10': clamping === TradePartnerNameAndLogoClamping.VERTICAL,
                  'max-w-sm text-ellipsis': clamping === TradePartnerNameAndLogoClamping.HORIZONTAL,
                },
              )}
            >
              {partner?.name || t('common:tradePartner.unknownName')}
            </span>
          </TooltipTrigger>
          <TooltipContent>{partner?.name || t('common:tradePartner.unknownName')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

const getPartnerLogoUrl = (partner: PartnerWithLogo | PartnerDTO) => {
  return isPartnerV2(partner) ? partner.logo_url : partner.logoUrl;
};

const isPartnerV2 = (partner: PartnerWithLogo | PartnerDTO): partner is PartnerWithLogo => {
  return 'logo_url' in partner;
};
